import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { DataItem, updateDailyRental } from '../../services/apiSLTP/activities';
import { createDailyRental, DailyRental, deleteDailyRental } from '../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getPriceTypeInfos, materialState, stepModes, systemConfigKeyEnum } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import ManageDailyRentalModal from '../modals/operations/planning/ManageDailyRentalModal';
import IconButton from '../shared/IconButton';
import { useSystemConfig } from '../../hooks/useSystemConfig';

interface RentalsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
    createTransfer?: boolean;
}

const RentalsCell: React.FC<RentalsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
    createTransfer = true,
}) => {
    const itemType = 'RENTALS';
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const manageModal = useBoolean(false);
    const [rental, setRental] = useState<DailyRental | null>(null);

    const countDuplicateRental = (rentalDesignation: string, companyId: string) => {
        const matchingRentalIds = activities
            .flatMap((activity) => activity.dailyRentals)
            .filter(
                (dailyRental) =>
                    dailyRental.designation.toLowerCase() === rentalDesignation.toLowerCase() && dailyRental.rentalTypeId._id === companyId
            );
        if (matchingRentalIds.length === 1) return 1;
        if (
            matchingRentalIds.length === 2 &&
            (matchingRentalIds[0].state === materialState.NEW || matchingRentalIds[1].state === materialState.NEW) &&
            (matchingRentalIds[0].state === materialState.REMOVE || matchingRentalIds[1].state === materialState.REMOVE)
        )
            return 1;
        return matchingRentalIds.length;
    };

    const handleRentalsChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailyRental(fromItemId, { activityId: toLineId, createTransfer: true }, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error updating daily rental:', error);
        }
    };

    const handleDeleteRental = async (id: string, index: number) => {
        try {
            await deleteDailyRental(id, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error deleting daily rental:', error);
        }
    };

    const handleCreateDailyRental = async (dailyRentalId: string) => {
        const selectedRental = activity.dailyRentals.find((value) => value._id === dailyRentalId);
        if (selectedRental) {
            const response = await createDailyRental({
                activityId: activity._id,
                rentalTypeId: selectedRental.rentalTypeId._id,
                designation: selectedRental.designation,
                state: selectedRental.state,
                createTransfer,
                price: selectedRental.price,
                priceType: selectedRental.priceType,
            });
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleRentalsChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    setRental(null);
                                    manageModal.setTrue();
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addRental'))}
                            />
                        )}
                    </div>
                    <div className=" gap-5px fx-c">
                        {activity.dailyRentals.map((rental, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    key={`${activityIndex}-${index}`}
                                    dragIds={{ itemId: rental._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDeleteRental(rental._id, index)}
                                    disabled={disabled}
                                    state={rental.state}
                                    warning={countDuplicateRental(rental.designation, rental.rentalTypeId._id) > 1}
                                    warningChar={countDuplicateRental(rental.designation, rental.rentalTypeId._id).toString()}
                                    onDuplicate={() => handleCreateDailyRental(rental._id)}
                                    onDoubleClick={() => {
                                        setRental(rental);
                                        manageModal.setTrue();
                                    }}
                                >
                                    <div className=" one-line-container">
                                        <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                            <div className="one-line" title={`${rental.designation} - ${rental.rentalTypeId.designation}`}>
                                                {rental.designation} - {rental.rentalTypeId.designation}
                                            </div>
                                        </div>
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                                <div className="one-line" title={rental.rentalTypeId.codeProC}>
                                                    {rental.rentalTypeId.codeProC}
                                                </div>
                                            </div>
                                        )}
                                        {rental.observations && <div className="one-line">{rental.observations}</div>}
                                        {activity.stepMode === stepModes.VALIDATE &&
                                            typeof rental.price === 'number' &&
                                            typeof rental.priceType === 'number' && (
                                                <div
                                                    className="one-line font-italic"
                                                    style={{
                                                        color: '#DC4D23',
                                                        textDecorationLine:
                                                            rental?.isUsed && activity.includedInExpenses ? undefined : 'line-through',
                                                    }}
                                                >
                                                    {rental.price}{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}/{capitalizeFirstLetter(t(getPriceTypeInfos(rental.priceType)))}
                                                </div>
                                            )}
                                    </div>
                                </DraggableCell>
                                {(activity.stepMode === stepModes.REPORT || activity.stepMode === stepModes.VALIDATE) && (
                                    <div className="h-25px fx-r fx-items-c">
                                        <Checkbox
                                            checked={rental.isUsed}
                                            onChange={async (_, checked) => {
                                                await updateDailyRental(rental._id, { isUsed: checked });
                                                refetch();
                                            }}
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                            title={capitalizeFirstLetter(t('isUsed'))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>
            {manageModal.value && (
                <ManageDailyRentalModal
                    open={manageModal.value}
                    handleClose={manageModal.setFalse}
                    refetch={refetch}
                    dailyRental={rental}
                    activity={activity}
                    createTransfer={createTransfer}
                />
            )}
        </div>
    );
};

export default RentalsCell;
